///<reference types="webpack-env" />
import { configureStore, Action, ThunkAction } from '@reduxjs/toolkit';
import throttle from 'lodash/throttle';
import { Account, Transaction } from '@algoan/dashboard/model';
import { rootReducer } from './root-reducer';
import { nodeEnv } from '@algoan/dashboard/env';
import { parseJwtPayload } from '@algoan/dashboard/utils';
import { useDispatch as useReduxDispatch, useSelector as useReduxSelector } from 'react-redux';

declare global {
  interface Window {
    userEmail?: string;
  }
}

export type RootState = ReturnType<typeof rootReducer>;

export type RecursivePartial<T> = {
  [P in keyof T]?: RecursivePartial<T[P]>;
};

/**
 * remove confidential data from the state before sending it to sentry
 */
export const anonymizeState = (state: RootState): RecursivePartial<RootState> => ({
  auth: undefined,
  customer: { ...state.customer, customers: {}, customerList: [] },
  analysis: {
    ...state.analysis,
    analyses: state.analysis.analyses.map((analysis) => ({
      ...analysis,
      creditInsights: undefined,
      scores: undefined,
    })),
  },
  account: {
    ...state.account,
    accounts: state.account.accounts.map((account: Account) => ({
      id: account.id,
      type: account.type,
      usage: account.usage,
    })),
  },
  transaction: {
    ...state.transaction,
    transactions: state.transaction.transactions.map((transaction: Transaction) => ({
      id: transaction.id,
      accountId: transaction.accountId,
      dates: transaction.dates,
    })),
  },
  dashboardPage: {
    ...state.dashboardPage,
  },
});

export const loadState = <T extends Record<string, unknown>>(
  version: number | string,
  key = 'redux',
): T | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const serializedState = localStorage.getItem(key);
  if (serializedState === null) {
    return undefined;
  }

  const state = JSON.parse(serializedState);

  if (version !== state.version) {
    return undefined;
  }
  delete state.version;

  return state;
};

export const saveState = <T extends Record<string, unknown>>(state: T, key = 'redux'): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.setItem(key, JSON.stringify(state));
  } catch (err) {
    // ignore write errors
  }
};

export const clearLocalStorage = (key = 'redux'): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    localStorage.removeItem(key);
  } catch (err) {
    // ignore write errors
  }
};

// increment this in case of breaking change in the state
const STORE_VERSION = 5;

const persistedState = loadState(STORE_VERSION);

// TEMPORARY HACK for demo
// TODO: remove this after demo
if (typeof persistedState === 'object') {
  const initialState = persistedState as unknown as RootState;
  if (initialState.auth?.authInformation?.access_token) {
    try {
      const payload = parseJwtPayload(initialState.auth?.authInformation?.access_token);
      window.userEmail = payload.email;
    } catch {}
  }
}

export const store = configureStore({
  reducer: rootReducer,
  preloadedState:
    typeof persistedState === 'object'
      ? {
          ...persistedState,
          customer: undefined,
          analysis: undefined,
          account: undefined,
          transaction: undefined,
          appState: undefined,
          activity: undefined,
        }
      : undefined,
});

store.subscribe(
  throttle(() => {
    saveState({
      ...store.getState(),
      version: STORE_VERSION,
      customer: undefined,
      analysis: undefined,
      account: undefined,
      transaction: undefined,
      activity: undefined,
    });
  }, 1000),
);

if (nodeEnv === 'development' && module.hot) {
  module.hot.accept('./root-reducer', () => {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const newRootReducer = require('./root-reducer').default;
    store.replaceReducer(newRootReducer);
  });
}

export type Dispatch = typeof store.dispatch;

export type AsyncThunk<R = void> = ThunkAction<Promise<R>, RootState, undefined, Action<string>>;

export const useDispatch = (): Dispatch => useReduxDispatch<Dispatch>();

export const useSelector = <TSelected = unknown>(
  selector: (state: RootState) => TSelected,
  equalityFn?: (left: TSelected, right: TSelected) => boolean,
): TSelected => useReduxSelector<RootState, TSelected>(selector, equalityFn);
